/**
 * Hearing Quiz Funnel — postMessage bridge between Shopify storefront,
 * embedded quiz iframe (Next.js), and optional Tuned hearing test widget.
 *
 * Depends on: window.AudienHqfUtils, window.AUDIEN_HQF_CONFIG (from Liquid).
 */
(function () {
  'use strict';

  var cfg = window.AUDIEN_HQF_CONFIG;
  var U = window.AudienHqfUtils;
  if (!cfg || !U) return;

  /* ── Config ─────────────────────────────────────────────── */

  var QUIZ_IFRAME_URL = cfg.quizIframeUrl || '';
  var SHOW_HEARING_TEST = !!cfg.showHearingTest;

  var TUNED_AUDIOGRAM_RED_CODE = 3;
  var PERSIST_COOKIE_NAME = 'aq_hqf_state';
  var PERSIST_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 7;
  var PERSIST_QUIZ_PROGRESS_COOKIE_NAME = 'aq_hqf_quiz_progress';
  var SAFE_HEARING_LEVEL_PARAM_KEY = 'hearing_level';
  var SAFE_HEARING_SCORE_PARAM_KEY = 'hearing_score';
  var HEARING_RESULTS_BRIDGE_MESSAGE_TYPE = 'audien-hqf-hearing-results';
  var HEARING_RESULTS_BRIDGE_READY_EVENT_TYPES = [
    'audien-hqf-hearing-results-ready',
    'audien-hearing-results-ready',
    'hearing-results-ready'
  ];
  var ELEVAR_PUSH_MESSAGE_TYPE = 'elevar:push';
  var QUIZ_ATTRIBUTION_UTM_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term'
  ];
  var HEARING_ENCRYPTION_ALG = String(
    (cfg.hearingPayloadAlg || window.AUDIEN_HQF_HEARING_PAYLOAD_ALG || 'RSA-OAEP-256+A256GCM')
  ).trim();
  var HEARING_ENCRYPTION_KEY_ID = String(
    (cfg.hearingPayloadKeyId || window.AUDIEN_HQF_HEARING_PAYLOAD_KEY_ID || 'audien-hearing-payload-v1')
  ).trim();
  var HEARING_ENCRYPTION_PUBLIC_KEY_PEM = String(
    (cfg.hearingPayloadPublicKeyPem || window.AUDIEN_HQF_HEARING_PAYLOAD_PUBLIC_KEY_PEM || '')
  ).trim();
  var AUDIOGRAM_CODE_TO_COLOR = { 0: 'GREEN', 1: 'YELLOW', 2: 'ORANGE', 3: 'RED' };
  var AUDIOGRAM_COLOR_TO_CODE = { GREEN: 0, YELLOW: 1, ORANGE: 2, RED: 3 };
  var HEARING_LEVEL_BY_CODE = { 0: 'not_sure', 1: 'mild', 2: 'moderate', 3: 'complex' };
  var HEARING_LEVEL_BY_COLOR = { GREEN: 'not_sure', YELLOW: 'mild', ORANGE: 'moderate', RED: 'complex' };

  var hearingApiEmptyWarned = false;
  var hearingEncryptionPublicKeyPromise = null;

  function getHearingApiBaseUrl() {
    var u = (window.AUDIEN_HQF_CONFIG && window.AUDIEN_HQF_CONFIG.hearingApiUrl) || '';
    return String(u).trim();
  }

  function arrayBufferToBase64(buffer) {
    var bytes = new Uint8Array(buffer);
    var binary = '';
    var i;
    for (i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }

  function pemToArrayBuffer(pem) {
    var b64 = String(pem || '')
      .replace(/-----BEGIN PUBLIC KEY-----/g, '')
      .replace(/-----END PUBLIC KEY-----/g, '')
      .replace(/\s+/g, '');
    if (!b64) return null;
    var binary = atob(b64);
    var bytes = new Uint8Array(binary.length);
    var i;
    for (i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
  }

  async function getHearingEncryptionPublicKey() {
    if (hearingEncryptionPublicKeyPromise) return hearingEncryptionPublicKeyPromise;
    if (!HEARING_ENCRYPTION_PUBLIC_KEY_PEM) {
      throw new Error('[AudienFunnel] Missing hearing payload public key PEM.');
    }
    if (!window.crypto || !window.crypto.subtle) {
      throw new Error('[AudienFunnel] WebCrypto is unavailable for hearing payload encryption.');
    }
    var spki = pemToArrayBuffer(HEARING_ENCRYPTION_PUBLIC_KEY_PEM);
    if (!spki) throw new Error('[AudienFunnel] Invalid hearing payload public key PEM format.');
    hearingEncryptionPublicKeyPromise = window.crypto.subtle.importKey(
      'spki',
      spki,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt']
    );
    return hearingEncryptionPublicKeyPromise;
  }

  async function encryptHearingPayload(payload) {
    var publicKey = await getHearingEncryptionPublicKey();
    var plaintext = new TextEncoder().encode(JSON.stringify(payload || {}));
    var contentKey = await window.crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt']
    );
    var nonce = window.crypto.getRandomValues(new Uint8Array(12));
    var ciphertext = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: nonce },
      contentKey,
      plaintext
    );
    var rawContentKey = await window.crypto.subtle.exportKey('raw', contentKey);
    var encryptedKey = await window.crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      publicKey,
      rawContentKey
    );
    return {
      keyId: HEARING_ENCRYPTION_KEY_ID,
      alg: HEARING_ENCRYPTION_ALG,
      ciphertext: arrayBufferToBase64(ciphertext),
      nonce: arrayBufferToBase64(nonce.buffer),
      encryptedKey: arrayBufferToBase64(encryptedKey)
    };
  }

  var SHOPIFY_CUSTOMER_ID = cfg.shopifyCustomerId ? String(cfg.shopifyCustomerId) : '';
  var GA4_MEASUREMENT_ID = (cfg.ga4MeasurementId || '').trim();

  var DEFAULT_IFRAME_HEIGHT_PX = Math.max(480, window.innerHeight - 200);

  /* ── Visitor identity (GA4 preferred, UUID fallback) ───── */

  var ga4ClientId = null;
  var visitorId = '';

  function refreshVisitorId() {
    ga4ClientId = U.parseGa4ClientIdFromCookie()
      || U.readGa4ClientIdFromLocalStorage();

    if (ga4ClientId) {
      U.writeGa4ClientIdToLocalStorage(ga4ClientId);
      visitorId = ga4ClientId;
    } else {
      visitorId = U.getOrCreateFallbackVisitorUuid();
    }
    return visitorId;
  }

  function tryGtagLookup() {
    if (typeof gtag !== 'function' || !GA4_MEASUREMENT_ID) return;
    try {
      gtag('get', GA4_MEASUREMENT_ID, 'client_id', function (cid) {
        if (!cid) return;
        ga4ClientId = U.writeGa4ClientIdToLocalStorage(String(cid).trim());
        refreshVisitorId();
      });
    } catch (_) {}
  }

  refreshVisitorId();

  /* ── Product catalog (results step) ────────────────────── */

  var PRODUCTS = {
    atom_one:   { name: 'Atom ONE',   price: '$98',  url: '/products/audien-atom-one-hearing-aid',    formFactor: 'ITE' },
    atom_2:     { name: 'Atom 2',     price: '$189', url: '/products/audien-atom-2-hearing-aid',      formFactor: 'ITE' },
    atom_pro_2: { name: 'Atom Pro 2', price: '$289', url: '/products/audien-atom-pro-2-hearing-aid',  formFactor: 'ITE' },
    atom_x:     { name: 'Atom X',     price: '$389', url: '/products/audien-atom-x-hearing-aid',      formFactor: 'ITE' },
    ion2:       { name: 'ION2',       price: '$399', url: '/products/audien-ion2-hearing-aid',        formFactor: 'BTE' },
    ion_pro2:   { name: 'ION Pro2',   price: '$689', url: '/products/audien-ion-pro2-hearing-aid',    formFactor: 'BTE' }
  };

  var KEY_ALIASES = {
    atomone: 'atom_one', atom_1: 'atom_one', atom1: 'atom_one',
    atom2: 'atom_2', atom_ii: 'atom_2',
    atompro2: 'atom_pro_2', atom_pro2: 'atom_pro_2',
    atomx: 'atom_x',
    ion_pro_2: 'ion_pro2', ion_pro_two: 'ion_pro2', ionpro2: 'ion_pro2'
  };

  function normalizeProductKey(raw) {
    if (!raw) return '';
    var k = String(raw).trim().toLowerCase().replace(/[\s-]+/g, '_').replace(/([a-z])(\d)/g, '$1_$2');
    return PRODUCTS[k] ? k : (KEY_ALIASES[k] || k);
  }

  /* ── DOM refs ──────────────────────────────────────────── */

  var STEPS = SHOW_HEARING_TEST
    ? ['hearing-test', 'quiz', 'results']
    : ['quiz', 'results'];

  var stepEls = {
    'hearing-test': document.getElementById('hqf-step-hearing-test'),
    quiz:           document.getElementById('hqf-step-quiz'),
    results:        document.getElementById('hqf-step-results')
  };

  var indicators = {
    'hearing-test': document.getElementById('hqf-step-indicator-1'),
    quiz:           document.getElementById('hqf-step-indicator-2'),
    results:        document.getElementById('hqf-step-indicator-3')
  };

  var progressFill = document.getElementById('hqf-progress-fill');
  var progressRoot = document.getElementById('hqf-progress');
  var quizIframe   = document.getElementById('hqf-quiz-iframe');
  var simulateBtn  = document.getElementById('hqf-simulate-hearing-complete');
  var tunedWidgetWrap = document.getElementById('hqf-tuned-widget-wrap');
  var tunedStepZero = document.getElementById('hqf-tuned-step-zero');
  var hearingInlineResult = document.getElementById('hqf-hearing-inline-result');
  var hearingInlineResultHeading = document.getElementById('hqf-hearing-inline-result-heading');
  var hearingInlineResultBadge = document.getElementById('hqf-hearing-inline-result-badge');
  var hearingInlineResultBody = document.getElementById('hqf-hearing-inline-result-body');
  var hearingInlineResultMeta = document.getElementById('hqf-hearing-inline-result-meta');
  var hearingTestContinueWrap = document.getElementById('hqf-hearing-test-continue-wrap');
  var hearingTestContinueBtn = document.getElementById('hqf-hearing-test-continue');

  var TUNED_COMPLETE_DEBOUNCE_MS = 2500;
  var tunedCompleteLastAt = 0;
  var latestHearingPrefill = null;
  var latestHearingCollectedData = null;
  var persistedState = {};
  var latestQuizProgress = null;
  var hearingResultsBridgeSentForCurrentIframe = false;
  var hearingResultsBridgeLoadToken = 0;

  function setCookie(name, value, maxAgeSeconds) {
    try {
      var encoded = encodeURIComponent(value);
      document.cookie = name + '=' + encoded + '; Path=/; Max-Age=' + String(maxAgeSeconds) + '; SameSite=Lax';
    } catch (_) {}
  }

  function getCookie(name) {
    try {
      var prefix = name + '=';
      var parts = (document.cookie || '').split(';');
      var i;
      for (i = 0; i < parts.length; i++) {
        var c = String(parts[i] || '').trim();
        if (c.indexOf(prefix) === 0) return decodeURIComponent(c.slice(prefix.length));
      }
    } catch (_) {}
    return '';
  }

  function readPersistedState() {
    try {
      var raw = getCookie(PERSIST_COOKIE_NAME);
      if (!raw) return {};
      var data = JSON.parse(raw);
      return (data && typeof data === 'object') ? data : {};
    } catch (_) {
      return {};
    }
  }

  function persistState(partialState) {
    var incoming = partialState || {};
    var nextStage = incoming.stage || (persistedState && persistedState.stage) || null;
    persistedState = {
      stage: nextStage,
      skipHearingTest: !SHOW_HEARING_TEST
    };
    setCookie(PERSIST_COOKIE_NAME, JSON.stringify(persistedState), PERSIST_COOKIE_MAX_AGE_SECONDS);
  }

  function sanitizeQuizProgress(progress) {
    var p = progress || {};
    if (!p || typeof p !== 'object') return null;
    return {
      version: p.version || null,
      currentQuestionId: p.currentQuestionId || null,
      currentQuestionIndex: typeof p.currentQuestionIndex === 'number' ? p.currentQuestionIndex : null,
      isComplete: !!p.isComplete,
      updatedAt: p.updatedAt || null
    };
  }

  function persistQuizProgress(progress) {
    var sanitized = sanitizeQuizProgress(progress);
    latestQuizProgress = sanitized;
    if (!sanitized) return;
    setCookie(PERSIST_QUIZ_PROGRESS_COOKIE_NAME, JSON.stringify(sanitized), PERSIST_COOKIE_MAX_AGE_SECONDS);
    persistState({ stage: sanitized.isComplete ? 'quiz_completed' : 'quiz_started' });
  }

  /* ── Iframe height — only set from quiz postMessage ────── */

  function setIframeHeight(px) {
    if (!quizIframe) return;
    var h = (typeof px === 'number' && isFinite(px) && px > 0)
      ? Math.round(px)
      : DEFAULT_IFRAME_HEIGHT_PX;
    quizIframe.style.height = h + 'px';
  }

  /* ── Step navigation ───────────────────────────────────── */

  var currentStepIndex = 0;
  var screenEvents = [];

  async function goToStep(stepName) {
    var idx = STEPS.indexOf(stepName);
    if (idx === -1) return;
    currentStepIndex = idx;

    for (var key in stepEls) {
      var el = stepEls[key];
      var ind = indicators[key];
      if (!el) continue;

      var isActive = key === stepName;
      el.classList.toggle('is-active', isActive);
      isActive ? el.removeAttribute('aria-hidden') : el.setAttribute('aria-hidden', 'true');

      if (!ind) continue;
      var keyIndex = STEPS.indexOf(key);
      if (keyIndex === -1) {
        ind.classList.remove('is-active');
        ind.classList.remove('is-completed');
        ind.removeAttribute('aria-current');
        continue;
      }
      ind.classList.toggle('is-active', isActive);
      ind.classList.toggle('is-completed', keyIndex < idx);
      isActive ? ind.setAttribute('aria-current', 'step') : ind.removeAttribute('aria-current');
    }

    if (progressFill) {
      progressFill.style.width = (((idx + 1) / STEPS.length) * 100 + '%');
    }
    if (progressRoot) {
      progressRoot.style.display = '';
    }

    if (stepName === 'results') {
      var resultsEl = document.getElementById('hqf-step-results');
      if (resultsEl) resultsEl.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  /* ── Quiz iframe src builder ───────────────────────────── */

  function buildQuizSrc() {
    refreshVisitorId();
    var sep = QUIZ_IFRAME_URL.includes('?') ? '&' : '?';
    var src = QUIZ_IFRAME_URL + sep + 'visitorId=' + encodeURIComponent(visitorId);
    if (SHOPIFY_CUSTOMER_ID) {
      src += '&shopifyCustomerId=' + encodeURIComponent(SHOPIFY_CUSTOMER_ID);
    }
    if (ga4ClientId && ga4ClientId !== visitorId) {
      src += '&ga4ClientId=' + encodeURIComponent(ga4ClientId);
    }
    var hearingParams = latestHearingPrefill || {};
    var safeHearingLevel = hearingParams && hearingParams[SAFE_HEARING_LEVEL_PARAM_KEY] != null
      ? String(hearingParams[SAFE_HEARING_LEVEL_PARAM_KEY]).trim()
      : '';
    var safeHearingScore = hearingParams && hearingParams[SAFE_HEARING_SCORE_PARAM_KEY] != null
      ? String(hearingParams[SAFE_HEARING_SCORE_PARAM_KEY]).trim()
      : '';
    if (safeHearingLevel) {
      src += '&' + encodeURIComponent(SAFE_HEARING_LEVEL_PARAM_KEY) + '=' + encodeURIComponent(safeHearingLevel);
    } else if (safeHearingScore) {
      src += '&' + encodeURIComponent(SAFE_HEARING_SCORE_PARAM_KEY) + '=' + encodeURIComponent(safeHearingScore);
    }
    if (latestQuizProgress && latestQuizProgress.currentQuestionId) {
      src += '&' + encodeURIComponent('resume_question_id') + '=' + encodeURIComponent(String(latestQuizProgress.currentQuestionId));
    }
    if (latestQuizProgress && latestQuizProgress.currentQuestionIndex != null) {
      src += '&' + encodeURIComponent('resume_question_index') + '=' + encodeURIComponent(String(latestQuizProgress.currentQuestionIndex));
    }
    return appendQuizAttributionParams(src);
  }

  function getQuizIframeTargetOrigin() {
    if (!QUIZ_IFRAME_URL) return '';
    try {
      return new URL(QUIZ_IFRAME_URL, window.location.origin).origin;
    } catch (_) {
      return '';
    }
  }

  function isAllowedQuizIframeOrigin(origin) {
    var normalizedOrigin = String(origin || '');
    if (!normalizedOrigin) return false;

    var configuredOrigin = getQuizIframeTargetOrigin();
    if (!!configuredOrigin && normalizedOrigin === configuredOrigin) return true;

    return normalizedOrigin.indexOf('vercel.app') !== -1;
  }

  function pushElevarQuizEvent(payload) {
    if (!payload || typeof payload !== 'object') return;

    if (window.ElevarDataLayer && typeof window.ElevarDataLayer.push === 'function') {
      window.ElevarDataLayer.push(payload);
      return;
    }

    if (typeof window.ElevarPushToDataLayer === 'function') {
      window.ElevarPushToDataLayer(payload);
    }
  }

  function appendQuizAttributionParams(src) {
    var nextSrc = src;
    var pageUrl = window.location.href;
    var pagePath = window.location.pathname;

    nextSrc += '&pageUrl=' + encodeURIComponent(pageUrl);
    nextSrc += '&pagePath=' + encodeURIComponent(pagePath);

    var searchParams;
    try {
      searchParams = new URLSearchParams(window.location.search || '');
    } catch (_) {
      searchParams = null;
    }

    if (!searchParams) return nextSrc;

    QUIZ_ATTRIBUTION_UTM_PARAMS.forEach(function (paramName) {
      var paramValue = searchParams.get(paramName);
      if (!!paramValue) {
        nextSrc += '&' + encodeURIComponent(paramName) + '=' + encodeURIComponent(paramValue);
      }
    });

    return nextSrc;
  }

  function buildHearingResultsBridgePayload() {
    if (!SHOW_HEARING_TEST) return null;
    refreshVisitorId();
    var prefill = latestHearingPrefill || {};
    var hasPrefill = !!prefill.hearing_level || prefill.hearing_score != null || prefill.audiogram_summary_code != null;
    var hasCollectedData = !!latestHearingCollectedData;
    if (!hasPrefill && !hasCollectedData) return null;
    return {
      visitorId: visitorId || null,
      shopifyCustomerId: SHOPIFY_CUSTOMER_ID || null,
      ga4ClientId: ga4ClientId || null,
      testCompleted: true,
      completedScreenId: 'hearing_test_complete',
      hearingLevel: prefill.hearing_level || null,
      hearingScore: prefill.hearing_score != null ? prefill.hearing_score : null,
      audiogramSummaryCode: prefill.audiogram_summary_code != null ? prefill.audiogram_summary_code : null,
      audiogramSummaryColor: prefill.audiogram_summary_color || null,
      collectedData: latestHearingCollectedData || null
    };
  }

  function postHearingResultsToQuizIframe(reason) {
    if (!quizIframe || !quizIframe.contentWindow) return false;
    if (hearingResultsBridgeSentForCurrentIframe) return false;
    var payload = buildHearingResultsBridgePayload();
    if (!payload) return false;
    var targetOrigin = getQuizIframeTargetOrigin() || '*';
    try {
      quizIframe.contentWindow.postMessage(
        {
          type: HEARING_RESULTS_BRIDGE_MESSAGE_TYPE,
          payload: payload,
          source: 'audien-hearing-quiz-funnel',
          reason: reason || 'unknown'
        },
        targetOrigin
      );
      hearingResultsBridgeSentForCurrentIframe = true;
      return true;
    } catch (err) {
      console.error('[AudienFunnel] post hearing results bridge message:', err);
      return false;
    }
  }

  function loadQuizIframe() {
    if (!quizIframe || !QUIZ_IFRAME_URL) return;
    hearingResultsBridgeSentForCurrentIframe = false;
    hearingResultsBridgeLoadToken += 1;
    setIframeHeight(DEFAULT_IFRAME_HEIGHT_PX);
    quizIframe.src = buildQuizSrc();
  }

  /* ── API: save Tuned hearing test results (quiz saves from iframe app) ── */

  async function saveHearingResults(opts) {
    var base = getHearingApiBaseUrl();
    if (!base) {
      if (!hearingApiEmptyWarned) {
        hearingApiEmptyWarned = true;
        console.warn(
          '[AudienFunnel] hearingApiUrl is empty — skipping POST /api/save-hearing-test-results (expected https://audienhearingapi.azurewebsites.net from theme).'
        );
      }
      return;
    }
    refreshVisitorId();
    var o = opts || {};
    var url = base.replace(/\/$/, '') + '/api/save-hearing-test-results';
    var payload = {
      visitorId: visitorId,
      shopifyCustomerId: SHOPIFY_CUSTOMER_ID || null,
      ga4ClientId: ga4ClientId || null,
      screenEvents: screenEvents,
      completedScreenId: o.completedScreenId != null ? o.completedScreenId : null,
      testCompleted: !!o.testCompleted,
      collectedData: o.collectedData != null ? o.collectedData : null,
      dropOff: o.dropOff || null
    };
    var encryptedPayload = null;
    try {
      encryptedPayload = await encryptHearingPayload(payload);
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encryptedPayload)
      });
    } catch (err) {
      console.error('[AudienFunnel] save hearing results:', err);
    }
  }

  async function saveProductRecommendationResults(opts) {
    var base = getHearingApiBaseUrl();
    if (!base) {
      if (!hearingApiEmptyWarned) {
        hearingApiEmptyWarned = true;
        console.warn(
          '[AudienFunnel] hearingApiUrl is empty — skipping POST /api/save-product-recommendation-results (expected https://audienhearingapi.azurewebsites.net from theme).'
        );
      }
      return;
    }
    refreshVisitorId();
    var o = opts || {};
    var pii = (o.piiData && typeof o.piiData === 'object') ? o.piiData : {};
    var evaluationFor = pii.is_for_someone_else ? 'someone_else' : 'myself';
    var url = base.replace(/\/$/, '') + '/api/save-product-recommendation-results';
    var payload = {
      visitorId: visitorId,
      shopifyCustomerId: SHOPIFY_CUSTOMER_ID || null,
      ga4ClientId: ga4ClientId || null,
      Customer_name: pii.customer_name || null,
      customer_name: pii.customer_name || null,
      email: pii.customer_email || null,
      evaluation_for: pii.customer_email ? evaluationFor : null,
      evalution_for: pii.customer_email ? evaluationFor : null,
      MARKETING_CONSENT: pii.customer_email ? !!pii.marketing_opt_in : null,
      recipient_name: pii.is_for_someone_else ? (pii.recipient_name || null) : null,
      funnelStage: o.funnelStage || null
    };
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.error('[AudienFunnel] save product recommendation results:', err);
    }
  }

  /* ── Tuned audiogram code + Step Zero ─────────────────── */

  function extractAudiogramCode(collectedData) {
    if (!collectedData || typeof collectedData !== 'object') return null;
    var keys = ['audiogram_summary_code', 'audiogramSummaryCode', 'summary_code', 'audiogramCode', 'hearing_test_score', 'hearingTestScore'];
    var i;
    for (i = 0; i < keys.length; i++) {
      var v = collectedData[keys[i]];
      if (v != null && v !== '') return Number(v);
    }
    if (collectedData.summary && typeof collectedData.summary === 'object') {
      var s = collectedData.summary.audiogram_summary_code != null
        ? collectedData.summary.audiogram_summary_code
        : collectedData.summary.code;
      if (s != null && s !== '') return Number(s);
    }
    return null;
  }

  function extractAudiogramColor(collectedData) {
    if (!collectedData || typeof collectedData !== 'object') return '';
    var keys = ['audiogram_summary_color', 'audiogramSummaryColor', 'summary_color', 'audiogramColor'];
    var i;
    for (i = 0; i < keys.length; i++) {
      var v = collectedData[keys[i]];
      if (v != null && v !== '') return String(v).trim().toUpperCase();
    }
    if (collectedData.summary && typeof collectedData.summary === 'object') {
      var s = collectedData.summary.audiogram_summary_color != null
        ? collectedData.summary.audiogram_summary_color
        : collectedData.summary.color;
      if (s != null && s !== '') return String(s).trim().toUpperCase();
    }
    return '';
  }

  function extractHearingLevel(collectedData) {
    if (!collectedData || typeof collectedData !== 'object') return '';
    var v = collectedData.hearing_level != null ? collectedData.hearing_level : collectedData.hearingLevel;
    if (v == null || v === '') return '';
    var normalized = String(v).trim().toLowerCase();
    if (normalized === 'not_sure' || normalized === 'mild' || normalized === 'moderate' || normalized === 'complex') {
      return normalized;
    }
    return '';
  }

  function normalizeHearingPrefill(collectedData, opts) {
    var options = opts || {};
    var out = {};
    var code = options.audiogramSummaryCode;
    var color = options.audiogramSummaryColor || '';
    var hearingLevel = options.hearingLevel || '';

    if (code == null || code === '') code = extractAudiogramCode(collectedData);
    if (!color) color = extractAudiogramColor(collectedData);
    if (!hearingLevel) hearingLevel = extractHearingLevel(collectedData);

    if (code != null && code !== '' && isFinite(Number(code))) code = Number(code);
    else code = null;

    if (color) color = String(color).trim().toUpperCase();
    if (!color && code != null && AUDIOGRAM_CODE_TO_COLOR[code]) {
      color = AUDIOGRAM_CODE_TO_COLOR[code];
    }
    if (code == null && color && AUDIOGRAM_COLOR_TO_CODE[color] != null) {
      code = AUDIOGRAM_COLOR_TO_CODE[color];
    }
    if (!hearingLevel) {
      hearingLevel = color && HEARING_LEVEL_BY_COLOR[color]
        ? HEARING_LEVEL_BY_COLOR[color]
        : (code != null ? HEARING_LEVEL_BY_CODE[code] : '');
    }
    if (hearingLevel) out.hearing_level = hearingLevel;
    if (color) out.audiogram_summary_color = color;
    if (code != null) {
      out.audiogram_summary_code = code;
      out.hearing_score = code;
    }
    return out;
  }

  function getHearingSummaryBodyHtml(color) {
    var copy = (cfg && cfg.hearingSummaryCopy) || {};
    if (color && copy[color]) return copy[color];
    return copy.FALLBACK || '';
  }

  function toTitleCase(value) {
    if (!value) return '';
    return String(value)
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function renderInlineHearingResults(collectedData, prefill) {
    if (!hearingInlineResult) return;
    var color = prefill && prefill.audiogram_summary_color ? String(prefill.audiogram_summary_color).toUpperCase() : '';
    var code = prefill && prefill.audiogram_summary_code != null ? Number(prefill.audiogram_summary_code) : null;
    var level = prefill && prefill.hearing_level ? String(prefill.hearing_level) : '';

    var headingByColor = {
      GREEN: 'No Hearing Aid Needed',
      YELLOW: 'Suitable for OTC',
      ORANGE: 'Suitable for OTC',
      RED: 'Not Suitable for OTC'
    };
    if (hearingInlineResultHeading) {
      hearingInlineResultHeading.textContent = headingByColor[color] || 'Your Hearing Test Results';
    }
    if (hearingInlineResultBadge) {
      hearingInlineResultBadge.className = 'hqf-hearing-inline-result__badge';
      hearingInlineResultBadge.textContent = color ? color + ' RESULT' : 'RESULT READY';
      if (color) hearingInlineResultBadge.classList.add('hqf-hearing-inline-result__badge--' + color.toLowerCase());
    }
    if (hearingInlineResultBody) {
      hearingInlineResultBody.innerHTML = getHearingSummaryBodyHtml(color);
    }
    if (hearingInlineResultMeta) {
      var cd = collectedData || {};
      var details = [];
      if (code != null) details.push('Summary code: ' + code);
      if (level) details.push('Severity: ' + toTitleCase(level));
      if (cd.age) details.push('Age group: ' + toTitleCase(cd.age));
      if (cd.gender) details.push('Gender: ' + toTitleCase(cd.gender));
      if (cd.headphone_type) details.push('Headphone type: ' + toTitleCase(cd.headphone_type));
      if (cd.headphone_connection) details.push('Connection: ' + toTitleCase(cd.headphone_connection));
      hearingInlineResultMeta.innerHTML = details.map(function (row) {
        return '<li>' + row + '</li>';
      }).join('');
    }
    hearingInlineResult.hidden = false;
  }

  function revealHearingResultsAndContinue(resultData, prefill) {
    if (tunedWidgetWrap) tunedWidgetWrap.classList.add('hqf-tuned-widget-wrap--hidden');
    renderInlineHearingResults(resultData && resultData.collectedData, prefill);
    if (hearingTestContinueWrap) hearingTestContinueWrap.hidden = false;
    if (hearingTestContinueWrap) hearingTestContinueWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
    latestHearingCollectedData = resultData && resultData.collectedData ? resultData.collectedData : latestHearingCollectedData;
  }

  function showSevereHearingLossScreen() {
    if (tunedWidgetWrap) tunedWidgetWrap.classList.add('hqf-tuned-widget-wrap--hidden');
    if (hearingInlineResult) hearingInlineResult.hidden = true;
    if (hearingTestContinueWrap) hearingTestContinueWrap.hidden = true;
    if (tunedStepZero) {
      tunedStepZero.classList.add('is-visible');
      tunedStepZero.removeAttribute('hidden');
      tunedStepZero.setAttribute('aria-hidden', 'false');
      tunedStepZero.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  async function handleTunedRedDropOff(collectedData) {
    if (SHOW_HEARING_TEST) {
      await goToStep('hearing-test');
    }
    showSevereHearingLossScreen();
    await saveHearingResults({
      testCompleted: true,
      completedScreenId: 'test_complete_red',
      collectedData: collectedData,
      dropOff: 'red_complex_profile'
    });
    persistState({
      stage: 'red_dropoff'
    });
  }

  /* ── Results rendering ─────────────────────────────────── */

  function fillCard(prefix, product, description) {
    var card = document.getElementById('hqf-' + prefix + '-card');
    if (!card) return;
    card.style.display = '';

    var el = function (suffix) { return document.getElementById('hqf-' + prefix + '-' + suffix); };
    var nameEl  = el('name');
    var priceEl = el('price');
    var descEl  = el('description');
    var ctaEl   = el('cta');

    if (nameEl)  nameEl.textContent  = product.name;
    if (priceEl) priceEl.textContent = product.price + ' / pair';
    if (descEl)  descEl.textContent  = description || product.formFactor + ' hearing aid';
    if (ctaEl)   ctaEl.href          = product.url;
  }

  function renderResults(data) {
    var primaryCard   = document.getElementById('hqf-primary-card');
    var alternateCard = document.getElementById('hqf-alternate-card');
    var fallback      = document.getElementById('hqf-results-fallback');
    if (primaryCard)   primaryCard.style.display   = 'none';
    if (alternateCard) alternateCard.style.display = 'none';
    if (fallback)      fallback.style.display      = 'none';

    var pk = normalizeProductKey(data.primaryRecommendation || data.primary_product_key || data.primaryProductKey || data.primary);
    var ak = normalizeProductKey(data.alternativeRecommendation || data.alternative_product_key || data.alternativeProductKey || data.alternate || data.secondaryRecommendation);

    var shown = 0;
    if (pk && PRODUCTS[pk]) { fillCard('primary',   PRODUCTS[pk], data.primaryDescription || '');     shown++; }
    if (ak && PRODUCTS[ak]) { fillCard('alternate', PRODUCTS[ak], data.alternativeDescription || ''); shown++; }
    if (!shown && fallback) fallback.style.display = 'block';
  }

  function parseQuizPayload(msg) {
    var r = msg.results;
    if (r && typeof r === 'object' && !Array.isArray(r)) {
      return (r.data && typeof r.data === 'object') ? r.data : r;
    }
    var out = {};
    ['primaryRecommendation', 'alternativeRecommendation', 'primaryDescription', 'alternativeDescription'].forEach(function (k) {
      if (msg[k] != null && msg[k] !== '') out[k] = msg[k];
    });
    return out;
  }

  /* ── Public API ────────────────────────────────────────── */

  window.AudienFunnel = {
    goToStep: goToStep,
    loadQuizIframe: loadQuizIframe,

    async completeHearingTest(resultData) {
      var collected = resultData && resultData.collectedData;
      var codeFromResult = resultData && resultData.audiogramSummaryCode;
      var colorFromResult = resultData && resultData.audiogramSummaryColor;
      var summaryCode = codeFromResult != null && codeFromResult !== ''
        ? Number(codeFromResult)
        : extractAudiogramCode(collected);
      var summaryColor = colorFromResult || extractAudiogramColor(collected);
      var isRedProfile =
        summaryCode === TUNED_AUDIOGRAM_RED_CODE ||
        String(summaryColor || '').toUpperCase() === 'RED';
      if (isRedProfile) {
        await handleTunedRedDropOff(collected);
        return;
      }
      latestHearingPrefill = normalizeHearingPrefill(collected, {
        audiogramSummaryCode: resultData && resultData.audiogramSummaryCode,
        audiogramSummaryColor: resultData && resultData.audiogramSummaryColor,
        hearingLevel: resultData && resultData.hearingLevel
      });
      await saveHearingResults({
        testCompleted: true,
        completedScreenId: (resultData && resultData.screenId) || 'complete',
        collectedData: collected
      });
      await goToStep(SHOW_HEARING_TEST ? 'hearing-test' : 'quiz');
      if (SHOW_HEARING_TEST) revealHearingResultsAndContinue(resultData, latestHearingPrefill);
      persistState({
        stage: 'hearing_completed'
      });
      if (!SHOW_HEARING_TEST) loadQuizIframe();
    },

    async completeQuiz(resultData) {
      renderResults(resultData || {});
      await goToStep('results');
      setCookie(PERSIST_QUIZ_PROGRESS_COOKIE_NAME, '', 0);
      latestQuizProgress = null;
      persistState({ stage: 'quiz_completed' });
    }
  };

  /* ── postMessage bridge ────────────────────────────────── */

  window.addEventListener('message', async function (e) {
    var d = e.data;
    if (!d || !d.type) return;

    if (d.type === 'audien-quiz-resize') {
      var h = d.height != null ? d.height : (d.payload && d.payload.height);
      setIframeHeight(h);
    } else if (d.type === 'audien:quiz-progress' || d.type === 'audien-quiz-progress') {
      persistQuizProgress(d.payload || null);
    } else if (d.type === 'audien-quiz-complete') {
      await window.AudienFunnel.completeQuiz(parseQuizPayload(d));
    } else if (d.type === ELEVAR_PUSH_MESSAGE_TYPE && d.payload) {
      if (!isAllowedQuizIframeOrigin(e.origin)) return;
      pushElevarQuizEvent(d.payload);
    } else if (d.type === 'tuned-test-complete') {
      await window.AudienFunnel.completeHearingTest(d.results || {});
    } else if (HEARING_RESULTS_BRIDGE_READY_EVENT_TYPES.indexOf(String(d.type)) !== -1) {
      postHearingResultsToQuizIframe('iframe_ready_message');
    }
  });

  /*
   * Tuned Web Hearing Test (production) — align with cdn.audiocare.app/internal/audien/web.html:
   * init → setConfigId("audien_web_prod") in snippet; audien_ready; audien_active_screen;
   * audien_test_complete with collected_data (audiogram summary 0/1/2/3; 3 = RED → Step Zero).
   *
   * Legacy names (active_screen, test_complete, progress) still handled for older widget builds.
   * Canonical completion is audien_test_complete / test_complete; progress testFinished is fallback
   * when collected_data may be absent.
   *
   * widget.js replaces tuned.widget with WidgetAPI (no .on); subscriptions queue on stub _calls.
   */
  /* ── Tuned widget (bootstrap in Liquid) ───────── */

  function normalizeTunedCollectedData(event) {
    var e = event || {};
    if (e.collected_data != null) return e.collected_data;
    if (e.collectedData != null) return e.collectedData;
    return null;
  }

  function onTunedTestComplete(event) {
    var now = Date.now();
    if (now - tunedCompleteLastAt < TUNED_COMPLETE_DEBOUNCE_MS) return;
    tunedCompleteLastAt = now;
    void (async function () {
      var collected = normalizeTunedCollectedData(event);
      var code = extractAudiogramCode(collected);
      var color = extractAudiogramColor(collected);
      var isRedProfile =
        code === TUNED_AUDIOGRAM_RED_CODE ||
        String(color || '').toUpperCase() === 'RED';
      if (isRedProfile) {
        await handleTunedRedDropOff(collected);
        return;
      }
      await window.AudienFunnel.completeHearingTest({
        screenId: (event && event.screenId) || 'test_complete',
        collectedData: collected,
        audiogramSummaryCode: code,
        audiogramSummaryColor: color
      });
    })().catch(function (err) {
      console.error('[AudienFunnel] test_complete', err);
    });
  }

  function normalizeTunedScreenId(event) {
    var e = event || {};
    if (e.screen_id != null) return e.screen_id;
    if (e.screenId != null) return e.screenId;
    return null;
  }

  function isTunedResultsLikeScreenId(sid) {
    if (sid == null) return false;
    var s = String(sid).trim().toLowerCase().replace(/_/g, '-');
    return s === 'results' || s === 'calculate-results';
  }

  function onTunedActiveScreenEvent(event) {
    var sid = normalizeTunedScreenId(event);
    if (sid != null) {
      screenEvents.push({ screenId: String(sid), timestamp: new Date().toISOString() });
    }
    if (isTunedResultsLikeScreenId(sid)) {
      onTunedTestComplete({
        collected_data: normalizeTunedCollectedData(event),
        collectedData: event && event.collectedData
      });
    }
  }

  function onTunedBridgeEvent(ev) {
    if (!SHOW_HEARING_TEST) return;
    var d = ev && ev.detail;
    if (!d || !d.name) return;
    var name = d.name;
    if (name === 'init') {
      refreshVisitorId();
      var api = d.api;
      if (api && typeof api.setVisitorId === 'function') {
        api.setVisitorId(visitorId);
      }
      return;
    }
    if (name === 'audien_ready') {
      var tunedRoot = document.getElementById('tuned-widget');
      if (tunedRoot) tunedRoot.classList.add('loaded');
      return;
    }
    if (name === 'audien_active_screen') {
      onTunedActiveScreenEvent(d.event);
      return;
    }
    if (name === 'audien_test_complete') {
      onTunedTestComplete(d.event);
      return;
    }
    if (name === 'progress') {
      var milestone = d.event;
      if (milestone === 'testFinished') {
        onTunedTestComplete({
          screenId: 'progress_testFinished',
          collectedData: { _source: 'tuned_progress', milestone: String(milestone) }
        });
        return;
      }
      if (milestone != null && milestone !== '') {
        screenEvents.push({
          screenId: 'progress:' + String(milestone),
          timestamp: new Date().toISOString()
        });
      }
      return;
    }
    if (name === 'active_screen' || name === 'activeScreen') {
      onTunedActiveScreenEvent(d.event);
      return;
    }
    if (name === 'test_complete' || name === 'testComplete') {
      onTunedTestComplete(d.event);
    }
  }

  window.addEventListener('audien-hqf-tuned', onTunedBridgeEvent);
  var _tunedPre = window.__AUDIEN_HQF_TUNED_PRELOAD;
  if (_tunedPre && _tunedPre.length) {
    _tunedPre.splice(0).forEach(function (e) {
      onTunedBridgeEvent(e);
    });
  }
  window.__AUDIEN_HQF_TUNED_BRIDGE_READY = true;

  /* ── Init ──────────────────────────────────────────────── */

  if (simulateBtn) {
    simulateBtn.addEventListener('click', async function () {
      await window.AudienFunnel.completeHearingTest({ simulated: true, collectedData: { simulated: true } });
    });
  }

  Object.keys(indicators).forEach(function (key) {
    var el = indicators[key];
    if (!el) return;
    el.addEventListener('click', function () {
      if (STEPS.indexOf(key) <= currentStepIndex) goToStep(key);
    });
  });

  document.addEventListener('DOMContentLoaded', function () {
    persistedState = readPersistedState();
    persistedState = {
      stage: persistedState && persistedState.stage ? persistedState.stage : null,
      skipHearingTest: !SHOW_HEARING_TEST
    };
    persistState({ stage: persistedState.stage });
    var rawQuizProgress = getCookie(PERSIST_QUIZ_PROGRESS_COOKIE_NAME);
    if (rawQuizProgress) {
      try {
        latestQuizProgress = sanitizeQuizProgress(JSON.parse(rawQuizProgress));
      } catch (_) {
        latestQuizProgress = null;
      }
    }

    refreshVisitorId();
    tryGtagLookup();
    [400, 1200, 3000].forEach(function (ms) {
      setTimeout(function () { refreshVisitorId(); tryGtagLookup(); }, ms);
    });

    if (SHOW_HEARING_TEST) {
      if (persistedState.stage === 'quiz_started' || persistedState.stage === 'quiz_completed' || (latestQuizProgress && !latestQuizProgress.isComplete)) {
        goToStep('quiz');
        loadQuizIframe();
      } else if (persistedState.stage === 'red_dropoff') {
        goToStep('hearing-test');
        showSevereHearingLossScreen();
      } else if (persistedState.stage === 'hearing_completed') {
        goToStep('hearing-test');
        if (tunedWidgetWrap) tunedWidgetWrap.classList.add('hqf-tuned-widget-wrap--hidden');
        if (hearingInlineResult) hearingInlineResult.hidden = false;
        if (latestHearingPrefill || latestHearingCollectedData) {
          renderInlineHearingResults(latestHearingCollectedData || {}, latestHearingPrefill || {});
        }
        if (hearingTestContinueWrap) hearingTestContinueWrap.hidden = false;
      } else {
        goToStep('hearing-test');
      }
      if (persistedState.stage !== 'hearing_completed' && persistedState.stage !== 'red_dropoff') {
        if (tunedWidgetWrap) tunedWidgetWrap.classList.remove('hqf-tuned-widget-wrap--hidden');
        if (hearingInlineResult) hearingInlineResult.hidden = true;
        if (hearingTestContinueWrap) hearingTestContinueWrap.hidden = true;
      }
    } else {
      goToStep('quiz');
      loadQuizIframe();
    }

    if (hearingTestContinueBtn) {
      hearingTestContinueBtn.addEventListener('click', async function () {
        await goToStep('quiz');
        persistState({ stage: 'quiz_started' });
        await saveProductRecommendationResults({
          funnelStage: 'quiz_started'
        });
        loadQuizIframe();
      });
    }

    if (quizIframe) {
      quizIframe.addEventListener('load', function () {
        var currentLoadToken = hearingResultsBridgeLoadToken;
        setTimeout(function () {
          if (currentLoadToken !== hearingResultsBridgeLoadToken) return;
          postHearingResultsToQuizIframe('iframe_load_fallback');
        }, 500);
      });
    }
  });
})();
